import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PMM_LEVELS, PMM_DIMENSIONS, formatCr, pmmLevelForScore } from "@/lib/utils-ros";
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from "recharts";
import { Award, Plus, Loader2, UserPlus, Building2, X, Pencil, Check, Upload, Image as ImageIcon, Search, Trash2, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function PMMRadar({ scores }) {
  const data = PMM_DIMENSIONS.map(d => ({ subject: d.label, value: scores[d.key] || 0, fullMark: 100 }));
  return (
    <ResponsiveContainer width="100%" height={220}>
      <RadarChart data={data}>
        <PolarGrid stroke="#C9DCE6" />
        <PolarAngleAxis dataKey="subject" tick={{ fill: "#607D8B", fontSize: 9 }} />
        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
        <Radar name="Score" dataKey="value" stroke="#07539A" fill="#00A2D4" fillOpacity={0.22} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

export default function Partners() {
  const { role } = useAuth();
  const canManage = role === "super_admin" || role === "admin" || role === "founder";
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [query, setQuery] = useState("");
  const [firmOpen, setFirmOpen] = useState(false);
  const [editingPMM, setEditingPMM] = useState(false);
  const [pmmDraft, setPmmDraft] = useState(null);
  const [savingPMM, setSavingPMM] = useState(false);
  // When false the level tracks the score automatically; a manual pick overrides it.
  const [levelOverridden, setLevelOverridden] = useState(false);
  const [firmUsers, setFirmUsers] = useState([]);
  const [firmUsersLoading, setFirmUsersLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/partners");
      setPartners(r.data);
      if (r.data.length && !selected) setSelected(r.data[0]);
    } catch {
      toast.error("Could not load partners");
      setPartners([]);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    if (canManage) load();
    else setLoading(false);
  }, [canManage]);

  useEffect(() => {
    if (!selected?.id) return;
    // Leaving a partner cancels any in-progress PMM edit.
    setEditingPMM(false);
    setPmmDraft(null);
    setLevelOverridden(false);
  }, [selected?.id]);

  useEffect(() => {
    if (!selected?.id) { setFirmUsers([]); return; }
    let cancelled = false;
    setFirmUsersLoading(true);
    api.get(`/partners/${selected.id}`)
      .then(({ data }) => { if (!cancelled) setFirmUsers(data.users || []); })
      .catch(() => { if (!cancelled) setFirmUsers([]); })
      .finally(() => { if (!cancelled) setFirmUsersLoading(false); });
    return () => { cancelled = true; };
  }, [selected?.id]);

  async function deleteFirmUser(u) {
    if (!window.confirm(`Delete the login for ${u.name || u.email}? This can't be undone.`)) return;
    try {
      await api.delete(`/partners/${selected.id}/users/${u.id}`);
      toast.success("Login deleted");
      setFirmUsers(list => list.filter(x => x.id !== u.id));
    } catch (e) {
      toast.error(errMsg(e, "Failed to delete login"));
    }
  }

  function startEditPMM() {
    const scores = PMM_DIMENSIONS.reduce((acc, d) => {
      acc[d.key] = selected.pmm_scores?.[d.key] || 0;
      return acc;
    }, {});
    const level = selected.pmm_level || "explorer";
    // If the stored level already matches the score, treat it as auto-linked;
    // otherwise it was set manually, so keep it as an override.
    setLevelOverridden(level !== pmmLevelForScore(selected.pmm_overall || 0));
    setPmmDraft({ pmm_level: level, pmm_scores: scores });
    setEditingPMM(true);
  }

  function setDraftScore(key, v) {
    const n = Math.max(0, Math.min(100, Number.isNaN(parseInt(v, 10)) ? 0 : parseInt(v, 10)));
    setPmmDraft(d => {
      const scores = { ...d.pmm_scores, [key]: n };
      const overall = Math.round(PMM_DIMENSIONS.reduce((s, dim) => s + (scores[dim.key] || 0), 0) / PMM_DIMENSIONS.length);
      // Keep the level linked to the score unless the user overrode it.
      return { ...d, pmm_scores: scores, pmm_level: levelOverridden ? d.pmm_level : pmmLevelForScore(overall) };
    });
  }

  function pickLevel(v) {
    setLevelOverridden(true);
    setPmmDraft(d => ({ ...d, pmm_level: v }));
  }

  function resetLevelToAuto() {
    setLevelOverridden(false);
    setPmmDraft(d => ({ ...d, pmm_level: pmmLevelForScore(draftOverall) }));
  }

  async function savePMM() {
    setSavingPMM(true);
    try {
      const { data } = await api.patch(`/partners/${selected.id}/pmm`, pmmDraft);
      toast.success("PMM updated");
      setSelected(data);
      setEditingPMM(false);
      setPmmDraft(null);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Failed to update PMM"));
    } finally { setSavingPMM(false); }
  }

  const draftOverall = pmmDraft
    ? Math.round(PMM_DIMENSIONS.reduce((s, d) => s + (pmmDraft.pmm_scores[d.key] || 0), 0) / PMM_DIMENSIONS.length)
    : 0;

  const filteredPartners = partners.filter((partner) => {
    const needle = query.trim().toLowerCase();
    if (!needle) return true;
    return [partner.name, partner.firm, partner.region]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(needle);
  });

  if (!canManage) return <div className="min-h-full bg-[#F5FAFC] p-10 text-center text-[#607D8B]">Partner management is restricted to Admins.</div>;
  if (loading) return <div className="min-h-full bg-[#F5FAFC] p-8 text-[#607D8B]">Loading partners…</div>;

  return (
    <div className="min-h-full bg-[#F5FAFC] pb-12" data-testid="partners-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-7 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight !text-white">Partner Management</h1>
            <p className="text-[13px] text-white/75 mt-1">Admin-only partner firms, maturity scoring, and operational controls.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right pr-1">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Partner revenue YTD</div>
              <div className="font-display text-2xl tabular-nums !text-white">
              {formatCr(partners.reduce((s, p) => s + (p.ytd_revenue_cr || 0), 0))}
            </div>
          </div>
          {canManage && (
            <button
              onClick={() => setFirmOpen(true)}
              data-testid="add-partner-firm-btn"
              className="h-10 px-4 rounded-xl bg-[#E58A19] hover:bg-[#C96F08] !text-white text-[12px] font-semibold flex items-center gap-2 shadow-[0_7px_18px_rgba(0,0,0,0.18)] transition-colors"
            >
              <Building2 size={13} /> Add Partner Firm
            </button>
          )}
        </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10">
        <div className="max-w-3xl mx-auto h-14 px-6 rounded-full bg-white border border-[#BFD5E0] shadow-[0_12px_30px_rgba(7,83,154,0.17)] flex items-center gap-3 focus-within:border-[#00A2D4] transition-colors">
          <Search size={18} className="text-[#07539A] shrink-0" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search for people or partner firms…"
            data-testid="partner-search"
            className="flex-1 bg-transparent outline-none text-[15px] text-[#243B53] placeholder:text-[#9AAEB8]"
          />
          <span className="text-[10px] font-mono text-[#78909C]">{filteredPartners.length}</span>
        </div>
      </div>

      <div className="px-4 md:px-8 pt-6 relative z-10 grid lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-1 bg-transparent" data-testid="partner-list">
          <div className="space-y-1 max-h-[70vh] overflow-y-auto pr-1">
            {filteredPartners.map(p => (
              <button
                key={p.id}
                onClick={() => setSelected(p)}
                data-testid={`partner-${p.id}`}
                className={`w-full text-left p-5 border-b border-[#C9DCE6] hover:bg-[#F3FAFD] transition-colors ${selected?.id === p.id ? "bg-[#E7F5FB] rounded-2xl border-l-[6px] border-l-[#07539A] shadow-[0_7px_18px_rgba(7,83,154,0.08)]" : "border-l-[6px] border-l-transparent"}`}
              >
                <div className="flex items-center gap-3">
                  {p.avatar_url ? (
                    <img src={p.avatar_url} alt={p.name} className="w-11 h-11 rounded-xl object-cover shadow-sm" />
                  ) : (
                    <div className="w-11 h-11 rounded-xl bg-[#DDF1F8] flex items-center justify-center text-[#07539A] text-[12px] font-mono font-semibold">
                      {(p.firm || p.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-semibold text-[#12324A] truncate">{p.name}</div>
                    <div className="text-[10px] text-[#78909C] truncate">{p.firm} · {p.region}</div>
                  </div>
                </div>
              </button>
            ))}
            {filteredPartners.length === 0 && (
              <div className="p-8 text-center text-[12px] text-[#78909C]">No partners match your search.</div>
            )}
          </div>
        </div>

        {selected && (
          <div className="lg:col-span-2 space-y-4">
            <div className="border border-[#C9DCE6] rounded-[22px] bg-white p-5 shadow-[0_10px_28px_rgba(7,83,154,0.08)]" data-testid="partner-detail">
              <div className="flex items-start gap-4 mb-5">
                {selected.avatar_url ? (
                  <img src={selected.avatar_url} alt={selected.name} className="w-14 h-14 rounded-2xl object-cover shadow-sm" />
                ) : (
                  <div className="w-14 h-14 rounded-2xl bg-[#DDF1F8] flex items-center justify-center text-[#07539A] text-[16px] font-mono font-semibold">
                    {(selected.firm || selected.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="flex-1">
                  <div className="font-display text-xl text-[#12324A]">{selected.name}</div>
                  <div className="text-[12px] text-[#607D8B]">{selected.firm} · {selected.region}</div>
                  <div className="mt-2">
                    <span className="inline-flex px-3 py-1 rounded-full bg-[#DDF1F8] text-[#07539A] text-[10px] font-semibold uppercase tracking-wider">
                      L{PMM_LEVELS[selected.pmm_level]?.num} · {PMM_LEVELS[selected.pmm_level]?.label || selected.pmm_level}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl tabular-nums text-[#07539A]">{editingPMM ? draftOverall : selected.pmm_overall}</div>
                  <div className="text-[9px] uppercase tracking-widest text-[#78909C]">PMM Score</div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                <div className="px-4 py-3 rounded-2xl bg-[#EEF8FC] border border-[#D3E7EF]">
                  <div className="text-[9px] uppercase tracking-widest text-[#78909C]">YTD Revenue</div>
                  <div className="font-display text-lg tabular-nums text-[#07539A]">{formatCr(selected.ytd_revenue_cr)}</div>
                </div>
                <div className="px-4 py-3 rounded-2xl bg-[#EEF8FC] border border-[#D3E7EF]">
                  <div className="text-[9px] uppercase tracking-widest text-[#78909C]">Active Opps</div>
                  <div className="font-display text-lg tabular-nums text-[#07539A]">{selected.active_opportunities}</div>
                </div>
                <div className="px-4 py-3 rounded-2xl bg-[#EEF8FC] border border-[#D3E7EF]">
                  <div className="text-[9px] uppercase tracking-widest text-[#78909C]">Certifications</div>
                  <div className="font-display text-lg tabular-nums text-[#07539A]">{selected.certifications?.length || 0}</div>
                </div>
                <div className="px-4 py-3 rounded-2xl bg-[#EEF8FC] border border-[#D3E7EF]">
                  <div className="text-[9px] uppercase tracking-widest text-[#78909C]">Health</div>
                  <div className="font-display text-lg tabular-nums text-[#07539A]">{selected.health_score}</div>
                </div>
              </div>
            </div>

            <div className="border border-[#C9DCE6] rounded-[22px] bg-white overflow-hidden shadow-[0_8px_22px_rgba(7,83,154,0.06)]">
              <div className="px-4 py-3 bg-[#E7F5FB] flex items-center gap-2 border-b border-[#C9DCE6]">
                <Award size={15} className="text-[#07539A]" />
                <span className="font-display text-[16px] text-[#07539A]">Partner Maturity Model</span>
                {canManage && !editingPMM && (
                  <button
                    onClick={startEditPMM}
                    data-testid="edit-pmm-btn"
                    className="ml-auto h-9 px-4 rounded-xl bg-[#07539A] text-[11px] !text-white hover:bg-[#063F73] flex items-center gap-1.5 shadow-[0_5px_14px_rgba(7,83,154,0.18)]"
                  >
                    <Pencil size={12} /> Edit
                  </button>
                )}
                {editingPMM && (
                  <div className="ml-auto flex items-center gap-2">
                    <button
                      onClick={savePMM} disabled={savingPMM}
                      data-testid="save-pmm-btn"
                      className="text-[11px] px-3 py-2 rounded-xl bg-[#07539A] !text-white flex items-center gap-1 disabled:opacity-50"
                    >
                      {savingPMM ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Save
                    </button>
                    <button
                      onClick={() => { setEditingPMM(false); setPmmDraft(null); }}
                      data-testid="cancel-pmm-btn"
                      className="text-[11px] px-3 py-2 rounded-xl border border-[#BFD6E1] bg-white text-[#607D8B] hover:text-[#12324A] flex items-center gap-1"
                    >
                      <X size={11} /> Cancel
                    </button>
                  </div>
                )}
              </div>
              {editingPMM && (
                <div className="px-4 py-3 bg-[#F7FBFD] border-b border-[#DCEAF0] flex flex-wrap items-center gap-2">
                  <label className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">PMM level</label>
                  <select
                    value={pmmDraft.pmm_level}
                    onChange={event => pickLevel(event.target.value)}
                    data-testid="pmm-level-select"
                    className="h-9 px-3 rounded-xl bg-white border border-[#BFD6E1] focus:border-[#07539A] outline-none text-[12px]"
                  >
                    {Object.entries(PMM_LEVELS).map(([key, cfg]) => (
                      <option key={key} value={key}>L{cfg.num} · {cfg.label}</option>
                    ))}
                  </select>
                  {levelOverridden ? (
                    <button
                      type="button"
                      onClick={resetLevelToAuto}
                      data-testid="pmm-level-reset"
                      className="text-[10px] px-2.5 py-1.5 rounded-full bg-[#E7F5FB] text-[#07539A]"
                    >
                      Manual · reset to auto
                    </button>
                  ) : (
                    <span className="text-[10px] text-[#607D8B] inline-flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Auto from score
                    </span>
                  )}
                </div>
              )}
              <div className="p-3 bg-[#F9FCFD] border-b border-[#DCEAF0]">
                <PMMRadar scores={editingPMM ? pmmDraft.pmm_scores : (selected.pmm_scores || {})} />
              </div>
              <div className="px-4 py-2.5 bg-white border-b border-[#DCEAF0] text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">
                Dimension breakdown
              </div>
              <div className="divide-y divide-[#DCEAF0]">
                {PMM_DIMENSIONS.map(d => {
                  const score = editingPMM ? (pmmDraft.pmm_scores[d.key] || 0) : (selected.pmm_scores?.[d.key] || 0);
                  return (
                    <div key={d.key} className="flex items-center gap-3 px-4 py-3 hover:bg-[#F7FBFD]" data-testid={`pmm-dim-${d.key}`}>
                      <div className="w-40 text-[12px] text-[#243B53]">{d.label}</div>
                      {editingPMM ? (
                        <>
                          <input
                            type="range" min="0" max="100" value={score}
                            onChange={e => setDraftScore(d.key, e.target.value)}
                            data-testid={`pmm-range-${d.key}`}
                            className="flex-1 accent-[#07539A]"
                          />
                          <input
                            type="number" min="0" max="100" value={score}
                            onChange={e => setDraftScore(d.key, e.target.value)}
                            data-testid={`pmm-input-${d.key}`}
                            className="w-14 h-8 px-2 text-[12px] font-mono tabular-nums text-right rounded-lg border border-[#BFD6E1] focus:border-[#07539A] outline-none"
                          />
                        </>
                      ) : (
                        <>
                          <div className="flex-1 h-2 bg-[#E3EEF3] rounded-full overflow-hidden">
                            <div className="h-full bg-[#1687EA] rounded-full transition-all" style={{ width: `${score}%` }} />
                          </div>
                          <div className="font-mono text-[12px] tabular-nums w-8 text-right text-[#07539A]">{score}</div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="border border-[#C9DCE6] rounded-[22px] bg-white overflow-hidden shadow-[0_8px_22px_rgba(7,83,154,0.06)]" data-testid="firm-users-card">
              <div className="px-4 py-3 bg-[#E7F5FB] flex items-center gap-2 border-b border-[#C9DCE6]">
                <Users size={15} className="text-[#07539A]" />
                <span className="font-display text-[16px] text-[#07539A]">Login Accounts</span>
                <span className="text-[10px] font-mono text-[#78909C]">{firmUsers.length}</span>
              </div>
              {firmUsersLoading ? (
                <div className="p-6 text-center text-[12px] text-[#78909C] flex items-center justify-center gap-2"><Loader2 size={13} className="animate-spin" /> Loading…</div>
              ) : firmUsers.length === 0 ? (
                <div className="p-6 text-center text-[12px] text-[#78909C]">No one at this firm has product access yet.</div>
              ) : (
                <div className="divide-y divide-[#E2ECF1]">
                  {firmUsers.map(u => (
                    <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid={`firm-user-${u.id}`}>
                      <div className="w-9 h-9 rounded-xl bg-[#DDF1F8] flex items-center justify-center text-[#07539A] text-[11px] font-mono font-semibold shrink-0">
                        {(u.name || u.email || "?").slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12px] font-medium text-[#243B53] truncate">{u.name || "—"}</div>
                        <div className="text-[10px] text-[#78909C] truncate">{u.email}{u.is_deactivated ? " · deactivated" : ""}</div>
                      </div>
                      <button
                        onClick={() => deleteFirmUser(u)}
                        title="Delete login"
                        data-testid={`delete-firm-user-${u.id}`}
                        className="w-8 h-8 rounded-lg border border-[#E49B96] bg-white text-[#B42318] flex items-center justify-center hover:bg-[#FFF1F0]"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {firmOpen && <AddFirmModal onClose={() => setFirmOpen(false)} onCreated={(p) => { load(); setSelected(p); }} />}
    </div>
  );
}

function AddFirmModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "", firm: "", region: "North India", email: "", phone: "", pmm_level: "explorer", avatar_url: "",
  });
  const [seedUsers, setSeedUsers] = useState([{ email: "", name: "", password: "" }]);
  const [saving, setSaving] = useState(false);

  function updateUser(i, key, v) {
    setSeedUsers(us => us.map((u, idx) => idx === i ? { ...u, [key]: v } : u));
  }

  async function save() {
    if (!form.name.trim() || !form.firm.trim()) return toast.error("Firm name & primary contact required");
    setSaving(true);
    try {
      const users = seedUsers.filter(u => u.email && u.name).map(u => ({ email: u.email.trim(), name: u.name.trim(), password: u.password || undefined }));
      const payload = {
        firm: form.firm.trim(),
        name: form.name.trim(),
        region: form.region?.trim() || "North India",
        email: form.email?.trim() || undefined,
        phone: form.phone?.trim() || undefined,
        pmm_level: form.pmm_level,
        avatar_url: form.avatar_url || undefined,
        users,
      };
      const { data } = await api.post("/partners", payload);
      toast.success(`${data.partner.firm} added${users.length ? ` · ${data.users.length} user(s)` : ""}`);
      onCreated?.(data.partner);
      onClose();
    } catch (e) {
      toast.error(errMsg(e, "Failed to create partner"));
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#062E55]/55 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#EFF8FC] border border-[#BFD6E1] rounded-[24px] shadow-[0_28px_70px_rgba(7,42,73,0.28)] w-full max-w-xl overflow-hidden" onClick={e => e.stopPropagation()} data-testid="add-firm-modal">
        <div className="px-5 py-4 bg-[#07539A] flex items-center gap-2 !text-white">
          <span className="w-8 h-8 rounded-xl bg-white/12 flex items-center justify-center"><Building2 size={15} /></span>
          <div className="font-display text-[16px] flex-1 !text-white">Add Partner Firm</div>
          <button onClick={onClose} data-testid="add-firm-close" className="w-8 h-8 rounded-full border border-white/25 flex items-center justify-center hover:bg-white/15"><X size={14} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[72vh] overflow-y-auto">
          <AvatarPicker label="Firm / Partner Photo" value={form.avatar_url} onChange={v => setForm({ ...form, avatar_url: v })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Firm Name *" testid="firm-name" value={form.firm} onChange={v => setForm({ ...form, firm: v })} />
            <Field label="Primary Contact *" testid="firm-contact" value={form.name} onChange={v => setForm({ ...form, name: v })} />
            <Field label="Region" testid="firm-region" value={form.region} onChange={v => setForm({ ...form, region: v })} />
            <Field label="Email" testid="firm-email" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            <Field label="Phone" testid="firm-phone" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">Initial PMM Level</label>
              <select value={form.pmm_level} onChange={e => setForm({ ...form, pmm_level: e.target.value })} data-testid="firm-pmm-level"
                      className="w-full mt-1.5 h-10 px-3 rounded-xl bg-white border border-[#BFD6E1] focus:border-[#07539A] outline-none text-[13px]">
                {["explorer", "practitioner", "specialist", "growth_partner", "strategic_partner"].map(l => <option key={l}>{l}</option>)}
              </select>
            </div>
          </div>

          <div className="p-4 rounded-2xl bg-white border border-[#D1E4EC]">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus size={12} className="text-[#07539A]" />
              <span className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">Initial Team (optional)</span>
              <button
                onClick={() => setSeedUsers(us => [...us, { email: "", name: "", password: "" }])}
                data-testid="firm-add-user-row"
                className="ml-auto text-[11px] text-[#07539A] font-semibold flex items-center gap-1"
              >
                <Plus size={11} /> Add user
              </button>
            </div>
            {seedUsers.map((u, i) => (
              <div key={i} className="grid grid-cols-3 gap-2 mb-2" data-testid={`firm-user-row-${i}`}>
                <input placeholder="Name" value={u.name} onChange={e => updateUser(i, "name", e.target.value)} className="h-9 px-2.5 rounded-xl bg-[#F9FCFD] border border-[#BFD6E1] text-[12px] focus:border-[#07539A] outline-none" data-testid={`firm-user-name-${i}`} />
                <input placeholder="Email" value={u.email} onChange={e => updateUser(i, "email", e.target.value)} className="h-9 px-2.5 rounded-xl bg-[#F9FCFD] border border-[#BFD6E1] text-[12px] focus:border-[#07539A] outline-none" data-testid={`firm-user-email-${i}`} />
                <input placeholder="Password (optional)" value={u.password} onChange={e => updateUser(i, "password", e.target.value)} className="h-9 px-2.5 rounded-xl bg-[#F9FCFD] border border-[#BFD6E1] text-[12px] focus:border-[#07539A] outline-none" data-testid={`firm-user-password-${i}`} />
              </div>
            ))}
            <p className="text-[10px] text-[#78909C] mt-1">Blank passwords are auto-generated and shown once after creation.</p>
          </div>

          <button onClick={save} disabled={saving} data-testid="firm-save"
                  className="w-full h-11 rounded-xl bg-[#E58A19] hover:bg-[#C96F08] !text-white text-[13px] font-semibold disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_8px_20px_rgba(197,107,8,0.20)]">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create Firm
          </button>
        </div>
      </div>
    </div>
  );
}

// Turn any API error into a readable string. FastAPI validation errors return
// `detail` as an array of objects, which must never be handed to a toast/React.
function errMsg(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const msg = d.map(x => x?.msg).filter(Boolean).join("; ");
    if (msg) return msg;
  }
  return fallback;
}

// Read an image file and downscale it to a compact JPEG data URI so it can be
// stored inline in avatar_url without any file-storage backend.
function resizeImageToDataUrl(file, max = 256) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read failed"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("decode failed"));
      img.onload = () => {
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function AvatarPicker({ value, onChange, label = "Photo" }) {
  const [busy, setBusy] = useState(false);
  async function pick(e) {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after removal
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    setBusy(true);
    try {
      onChange(await resizeImageToDataUrl(file, 256));
    } catch {
      toast.error("Could not read that image");
    } finally { setBusy(false); }
  }
  return (
    <div className="flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-[#D1E4EC]">
      <div className="w-16 h-16 rounded-2xl border border-[#BFD6E1] bg-[#E7F5FB] overflow-hidden flex items-center justify-center shrink-0">
        {value ? <img src={value} alt="preview" className="w-full h-full object-cover" /> : <ImageIcon size={20} className="text-[#78909C]" />}
      </div>
      <div>
        <label className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">{label}</label>
        <div className="flex items-center gap-2 mt-1">
          <label className="cursor-pointer text-[11px] px-3 py-2 rounded-xl bg-[#E7F5FB] text-[#07539A] hover:bg-[#D6EDF6] flex items-center gap-1 font-semibold">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} {value ? "Change" : "Upload photo"}
            <input type="file" accept="image/*" className="hidden" onChange={pick} data-testid="avatar-input" />
          </label>
          {value && (
            <button type="button" onClick={() => onChange("")} data-testid="avatar-remove"
                    className="text-[11px] px-3 py-2 rounded-xl border border-[#F0B9B5] bg-white text-[#B42318] hover:bg-[#FFF1F0]">
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] font-semibold uppercase tracking-widest text-[#07539A]">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
             className="w-full mt-1.5 h-10 px-3 rounded-xl bg-white border border-[#BFD6E1] focus:border-[#07539A] outline-none text-[13px] shadow-[0_3px_8px_rgba(7,83,154,0.04)]" />
    </div>
  );
}
